import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TASK_CATEGORIES } from "@/lib/constants";

export default function AddTaskDialog({ open, onOpenChange, onSaved, task }) {
  const editing = Boolean(task?.id);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Personal");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(task?.title || "");
      setCategory(task?.category || "Personal");
      setDueDate(task?.due_date || "");
    }
  }, [open, task]);

  const save = async () => {
    if (!title.trim()) { toast.error("Please add a short task title."); return; }
    setSaving(true);
    try {
      const body = { title: title.trim(), category, due_date: dueDate || null };
      if (editing) await api.put(`/tasks/${task.id}`, body);
      else await api.post("/tasks", body);
      toast.success(editing ? "Task updated." : "Task added.");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit task" : "Add a task"}</DialogTitle>
          <DialogDescription>A small, concrete next step for your week.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Task</Label>
            <Input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="What gentle step is next?" className="rounded-xl" autoFocus
              onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-xl" data-testid="task-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {TASK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due date</Label>
              <Input type="date" data-testid="task-due-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-xl" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="task-save-button" className="rounded-full bg-primary hover:bg-primary/90">
            {editing ? "Save changes" : "Add task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
